import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Upload, FileText, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

const DOC_TYPES = ['MSME Certificate', 'Establishment Certificate', 'GST Certificate', 'PT Certificate', 'Networth Certificate', 'Notices', 'Other'];

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [clientFilter, setClientFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [uploadForm, setUploadForm] = useState({ client_id: '', document_type: '', expiry_date: '', file: null });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (clientFilter) params.set('client_id', clientFilter);
      if (typeFilter) params.set('document_type', typeFilter);
      if (searchText) params.set('search', searchText);
      const [docRes, clRes] = await Promise.all([api.get(`/documents?${params}`), api.get('/clients?limit=500')]);
      setDocuments(docRes.data);
      setClients(clRes.data.clients || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [clientFilter, typeFilter, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.client_id || !uploadForm.document_type) {
      toast.error('Client, document type, and file are required');
      return;
    }
    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('client_id', uploadForm.client_id);
    formData.append('document_type', uploadForm.document_type);
    formData.append('expiry_date', uploadForm.expiry_date || '');
    try {
      await api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setUploadDialog(false);
      setUploadForm({ client_id: '', document_type: '', expiry_date: '', file: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc._id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await api.delete(`/documents/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const getClientName = (clientId) => {
    const c = clients.find(cl => cl._id === clientId);
    return c?.full_name || 'Unknown';
  };

  const isExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  return (
    <div data-testid="documents-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Documents</h1>
          <p className="text-sm text-[#4F734F] mt-1">Manage client documents and certificates</p>
        </div>
        <Button onClick={() => setUploadDialog(true)} className="btn-accent gap-2" data-testid="upload-doc-btn">
          <Upload size={16} /> Upload Document
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search filename..."
          testId="doc-search"
          minWidth="min-w-[280px]"
          className="!flex-none w-full sm:w-80"
        />
        <Select value={clientFilter || 'all'} onValueChange={(v) => setClientFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48 h-11 input-field" data-testid="doc-client-filter"><SelectValue placeholder="All Clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map(c => <SelectItem key={c._id} value={c._id}>{c.full_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48 h-11 input-field" data-testid="doc-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <table className="data-table w-full">
          <thead><tr><th>Client</th><th>Type</th><th>Filename</th><th>Upload Date</th><th>Expiry Date</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-olive-400">Loading...</td></tr>
            ) : documents.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-olive-400">No documents found</td></tr>
            ) : documents.map(doc => (
              <tr key={doc._id}>
                <td className="font-medium text-[#111B11]">{getClientName(doc.client_id)}</td>
                <td><span className="bg-olive-100 text-olive-700 rounded-full px-2 py-0.5 text-xs">{doc.document_type}</span></td>
                <td className="flex items-center gap-2"><FileText size={14} className="text-olive-400" /><span className="truncate max-w-[200px]">{doc.original_filename}</span></td>
                <td>{doc.upload_date ? new Date(doc.upload_date).toLocaleDateString('en-IN') : '-'}</td>
                <td>
                  {doc.expiry_date ? (
                    <span className={isExpired(doc.expiry_date) ? 'text-red-600 font-medium' : 'text-[#273A27]'}>
                      {new Date(doc.expiry_date).toLocaleDateString('en-IN')}
                      {isExpired(doc.expiry_date) && ' (Expired)'}
                    </span>
                  ) : '-'}
                </td>
                <td>
                  <div className="flex gap-1">
                    <button onClick={() => handleDownload(doc)} className="p-1.5 hover:bg-olive-50 rounded-sm" data-testid={`download-doc-${doc._id}`}><Download size={14} className="text-olive-500" /></button>
                    <button onClick={() => handleDelete(doc._id)} className="p-1.5 hover:bg-red-50 rounded-sm" data-testid={`delete-doc-${doc._id}`}><Trash2 size={14} className="text-red-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Client</Label>
              <Select value={uploadForm.client_id || 'none'} onValueChange={(v) => setUploadForm(prev => ({ ...prev, client_id: v }))}>
                <SelectTrigger className="input-field" data-testid="upload-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent><SelectItem value="none" disabled>Select</SelectItem>{clients.map(c => <SelectItem key={c._id} value={c._id}>{c.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Document Type</Label>
              <Select value={uploadForm.document_type || 'none'} onValueChange={(v) => setUploadForm(prev => ({ ...prev, document_type: v }))}>
                <SelectTrigger className="input-field" data-testid="upload-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent><SelectItem value="none" disabled>Select</SelectItem>{DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Expiry Date (optional)</Label>
              <Input type="date" value={uploadForm.expiry_date} onChange={(e) => setUploadForm(prev => ({ ...prev, expiry_date: e.target.value }))} className="input-field" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">File</Label>
              <Input type="file" onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files[0] }))} className="input-field" data-testid="upload-file-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleUpload} className="btn-primary" data-testid="submit-upload-btn">Upload</Button>
              <Button variant="outline" onClick={() => setUploadDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
